import { useState } from 'react';
import {
  App,
  Badge,
  Button,
  Card,
  Dropdown,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps, MenuProps } from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  MobileOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  StopOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useDeactivateDevice, useDevices } from '@/hooks/useDevices';
import { useEnterprises } from '@/hooks/useEnterprises';
import type { DeviceDto } from '@/types/device';
import { DeviceStateColors, DeviceStateLabels, DeviceStateMap } from '@/types/device';
import { DeviceFormModal } from './DeviceFormModal';
import { CreateCommandModal } from '../commands/CreateCommandModal';
import { BulkOperationsModal } from './BulkOperationsModal';

const { Title, Text } = Typography;

export function DevicesPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [enterpriseFilter, setEnterpriseFilter] = useState<number | undefined>();
  const [stateFilter, setStateFilter] = useState<number | undefined>();

  const [formOpen, setFormOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<DeviceDto | null>(null);
  const [commandDevice, setCommandDevice] = useState<DeviceDto | null>(null);

  // Seleccion de filas para operaciones bulk
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading, refetch, isFetching } = useDevices({
    page,
    pageSize,
    search: search || undefined,
    includeInactive,
    enterpriseId: enterpriseFilter,
    state: stateFilter,
  });

  const { data: enterprises } = useEnterprises({ pageSize: 100 });
  const deactivate = useDeactivateDevice();

  const handleDeactivate = async (d: DeviceDto) => {
    try {
      await deactivate.mutateAsync(d.id);
      message.success('Dispositivo desactivado.');
    } catch {
      // interceptor
    }
  };

  const columns: TableProps<DeviceDto>['columns'] = [
    {
      title: 'Asignado a',
      key: 'assignedTo',
      render: (_, d) => (
        <Space direction="vertical" size={0}>
          <Space>
            <MobileOutlined style={{ color: 'var(--color-primary)' }} />
            <a onClick={() => navigate(`/devices/${d.id}`)}>
              <Text strong style={{ color: 'var(--color-primary)' }}>
                {d.assignedToName || <Text type="secondary" italic>Sin asignar</Text>}
              </Text>
            </a>
          </Space>
          {d.assignedToEmail && <Text type="secondary" style={{ fontSize: 12 }}>{d.assignedToEmail}</Text>}
          {d.assignedToPhone && <Text type="secondary" style={{ fontSize: 12 }}>{d.assignedToPhone}</Text>}
        </Space>
      ),
    },
    {
      title: 'Hardware',
      key: 'hardware',
      render: (_, d) => (
        <Space direction="vertical" size={0}>
          <Text>{d.brand && d.model ? `${d.brand} ${d.model}` : d.model || d.brand || '—'}</Text>
          {d.serialNumber && <Text code style={{ fontSize: 11 }}>SN: {d.serialNumber}</Text>}
          {d.imeiNumber && <Text code style={{ fontSize: 11 }}>IMEI: {d.imeiNumber}</Text>}
        </Space>
      ),
    },
    {
      title: 'Android',
      key: 'android',
      width: 110,
      render: (_, d) => d.androidVersion ? <Tag color="green">{d.androidVersion}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Enterprise',
      dataIndex: 'enterpriseName',
      key: 'enterpriseName',
      width: 160,
    },
    {
      title: 'Perfil',
      dataIndex: 'profileName',
      key: 'profileName',
      width: 160,
      render: (v) => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary" italic>Sin perfil</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'currentState',
      key: 'currentState',
      width: 130,
      render: (v: number) => {
        const state = DeviceStateMap[v];
        return <Tag color={DeviceStateColors[state]}>{DeviceStateLabels[state]}</Tag>;
      },
    },
    {
      title: 'Ultimo sync',
      dataIndex: 'lastSyncAt',
      key: 'lastSyncAt',
      width: 160,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <Text type="secondary">Nunca</Text>,
    },
    {
      title: 'Comandos',
      dataIndex: 'pendingCommandsCount',
      key: 'pendingCommandsCount',
      width: 110,
      align: 'center',
      render: (v) => v > 0 ? <Badge count={v} /> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (_, d) => {
        const items: MenuProps['items'] = [
          {
            key: 'detail',
            label: 'Ver detalle',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/devices/${d.id}`),
          },
          {
            key: 'command',
            label: 'Enviar comando',
            icon: <SendOutlined />,
            onClick: () => setCommandDevice(d),
          },
          {
            key: 'edit',
            label: 'Editar',
            icon: <EditOutlined />,
            onClick: () => {
              setEditingDevice(d);
              setFormOpen(true);
            },
          },
          { type: 'divider' as const },
          ...(d.isActive ? [{
            key: 'deactivate',
            label: 'Desactivar',
            icon: <StopOutlined />,
            danger: true,
            onClick: () => handleDeactivate(d),
          }] : []),
        ];
        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const stateOptions = Object.entries(DeviceStateMap).map(([k, v]) => ({
    value: Number(k),
    label: DeviceStateLabels[v],
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <MobileOutlined style={{ marginRight: 12 }} />
          Dispositivos
        </Title>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => {
            setEditingDevice(null);
            setFormOpen(true);
          }}
        >
          Nuevo Dispositivo
        </Button>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <Space wrap>
            <Input
              placeholder="Buscar por SN, IMEI, nombre..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => setPage(1)}
              style={{ width: 280 }}
              allowClear
            />
            <Select
              placeholder="Enterprise"
              allowClear
              value={enterpriseFilter}
              onChange={(v) => { setEnterpriseFilter(v); setPage(1); }}
              options={(enterprises?.items ?? []).map((e) => ({ value: e.id, label: e.displayNameEs }))}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Estado"
              allowClear
              value={stateFilter}
              onChange={(v) => { setStateFilter(v); setPage(1); }}
              options={stateOptions}
              style={{ width: 160 }}
            />
            <Button onClick={() => { setPage(1); refetch(); }}>Buscar</Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refrescar
            </Button>
          </Space>

          <Space>
            <Text>Incluir inactivos:</Text>
            <Switch checked={includeInactive} onChange={(v) => { setIncludeInactive(v); setPage(1); }} />
          </Space>
        </div>

        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 12, padding: 8, background: 'rgba(22,119,255,0.08)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text strong>{selectedRowKeys.length} dispositivo{selectedRowKeys.length !== 1 ? 's' : ''} seleccionado{selectedRowKeys.length !== 1 ? 's' : ''}</Text>
            <Space>
              <Button onClick={() => setSelectedRowKeys([])}>Limpiar</Button>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={() => setBulkOpen(true)}
              >
                Operacion masiva
              </Button>
            </Space>
          </div>
        )}

        <Table<DeviceDto>
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isLoading}
          scroll={{ x: 1400 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            preserveSelectedRowKeys: true,  // Mantiene seleccion entre paginas
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.totalCount ?? 0,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `${total} en total`,
          }}
        />
      </Card>

      <DeviceFormModal
        open={formOpen}
        device={editingDevice}
        onClose={() => {
          setFormOpen(false);
          setEditingDevice(null);
        }}
      />

      <CreateCommandModal
        device={commandDevice}
        onClose={() => setCommandDevice(null)}
      />

      <BulkOperationsModal
        open={bulkOpen}
        selectedDeviceIds={selectedRowKeys.map((k) => Number(k))}
        selectedEnterpriseId={enterpriseFilter}
        onClose={(changed) => {
          setBulkOpen(false);
          if (changed) {
            setSelectedRowKeys([]);
            refetch();
          }
        }}
      />
    </div>
  );
}
